import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExt from '@tiptap/extension-link';
import ImageExt from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '../components/ui/button';
import { ArrowLeft, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Quote, ImageIcon, Link2, Upload, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tags, setTags] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [metaDesc, setMetaDesc] = useState('');
  const [metaKw, setMetaKw] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('admin_token');
  const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExt.configure({ openOnClick: false }),
      ImageExt.configure({ inline: false }),
      Underline,
      Placeholder.configure({ placeholder: 'Začni pisati vsebino...' }),
    ],
    content: '',
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
    } catch { navigate('/admin'); }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    fetch(`${API}/api/categories`).then(r => r.json()).then(setCategories).catch(() => {});
    if (isEditing) {
      fetch(`${API}/api/posts/${id}`, { headers: authHeaders() }).then(r => r.json()).then(post => {
        setTitle(post.title || '');
        setExcerpt(post.excerpt || '');
        setCategoryId(post.category_id || '');
        setTags((post.tags || []).join(', '));
        setFeaturedImage(post.featured_image || '');
        setStatus(post.status || 'draft');
        setMetaDesc(post.meta_description || '');
        setMetaKw(post.meta_keywords || '');
        if (editor) editor.commands.setContent(post.content || '');
      }).catch(() => setError('Napaka pri nalaganju objave'));
    }
  }, [id, isEditing, editor]);

  const uploadImage = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/upload`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      setUploading(false);
      return data.url;
    } catch { setUploading(false); return null; }
  };

  const handleFeaturedUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setFeaturedImage(url);
  };

  const handleEditorImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const url = await uploadImage(file);
      if (url && editor) {
        const fullUrl = url.startsWith('/api') ? `${API}${url}` : url;
        editor.chain().focus().setImage({ src: fullUrl }).run();
      }
    };
    input.click();
  };

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url && editor) editor.chain().focus().setLink({ href: url }).run();
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Naslov je obvezen'); return; }
    setSaving(true);
    setError('');
    const payload = {
      title, excerpt, content: editor?.getHTML() || '',
      category_id: categoryId, tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      featured_image: featuredImage, status, meta_description: metaDesc, meta_keywords: metaKw
    };
    try {
      const res = await fetch(`${API}/api/posts${isEditing ? `/${id}` : ''}`, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Napaka pri shranjevanju');
      navigate('/admin/dashboard');
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/dashboard" className="flex items-center gap-1 text-stone-600 hover:text-violet-700 text-sm"><ArrowLeft size={16} />Nazaj</Link>
          <div className="flex items-center gap-2">
            <select value={status} onChange={e => setStatus(e.target.value)} className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white" data-testid="post-status">
              <option value="draft">Osnutek</option>
              <option value="published">Objavljeno</option>
            </select>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg" data-testid="save-post-btn">
              <Save size={16} className="mr-1" />{saving ? 'Shranjujem...' : 'Shrani'}
            </Button>
          </div>
        </div>

        {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg mb-4">{error}</div>}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Naslov objave" className="w-full text-2xl font-serif font-bold text-stone-800 bg-transparent border-none outline-none placeholder:text-stone-300" data-testid="post-title" />
            <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Kratek izvleček..." rows={2} className="w-full text-sm text-stone-600 bg-white border border-stone-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300 resize-none" data-testid="post-excerpt" />

            {/* Toolbar */}
            <div className="bg-white rounded-lg border border-stone-200 p-2 flex flex-wrap gap-1">
              <button onClick={() => editor?.chain().focus().toggleBold().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('bold') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><Bold size={16} /></button>
              <button onClick={() => editor?.chain().focus().toggleItalic().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('italic') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><Italic size={16} /></button>
              <button onClick={() => editor?.chain().focus().toggleUnderline().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('underline') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><UnderlineIcon size={16} /></button>
              <div className="w-px bg-stone-200 mx-1" />
              <button onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('heading', { level: 2 }) ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><Heading1 size={16} /></button>
              <button onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('heading', { level: 3 }) ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><Heading2 size={16} /></button>
              <div className="w-px bg-stone-200 mx-1" />
              <button onClick={() => editor?.chain().focus().toggleBulletList().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('bulletList') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><List size={16} /></button>
              <button onClick={() => editor?.chain().focus().toggleOrderedList().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('orderedList') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><ListOrdered size={16} /></button>
              <button onClick={() => editor?.chain().focus().toggleBlockquote().run()} className={`p-1.5 rounded hover:bg-stone-100 ${editor?.isActive('blockquote') ? 'bg-violet-100 text-violet-700' : 'text-stone-500'}`}><Quote size={16} /></button>
              <div className="w-px bg-stone-200 mx-1" />
              <button onClick={addLink} className="p-1.5 rounded hover:bg-stone-100 text-stone-500"><Link2 size={16} /></button>
              <button onClick={handleEditorImage} className="p-1.5 rounded hover:bg-stone-100 text-stone-500"><ImageIcon size={16} /></button>
              {uploading && <span className="text-xs text-violet-500 self-center ml-2">Nalagam...</span>}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg min-h-[300px]">
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-stone-200 p-4">
              <label className="block text-stone-700 text-sm font-medium mb-2">Kategorija</label>
              <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white" data-testid="post-category">
                <option value="">Brez kategorije</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4">
              <label className="block text-stone-700 text-sm font-medium mb-2">Oznake</label>
              <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="masaža, mama, dojenček" className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2" data-testid="post-tags" />
              <p className="text-stone-400 text-xs mt-1">Ločeno z vejicami</p>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4">
              <label className="block text-stone-700 text-sm font-medium mb-2">Naslovna slika</label>
              {featuredImage && (
                <div className="mb-2 aspect-video rounded-lg overflow-hidden bg-stone-100">
                  <img src={featuredImage.startsWith('/api') ? `${API}${featuredImage}` : featuredImage} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer text-violet-600 hover:text-violet-800 text-sm font-medium">
                <Upload size={16} />{featuredImage ? 'Zamenjaj sliko' : 'Naloži sliko'}
                <input type="file" accept="image/*" onChange={handleFeaturedUpload} className="hidden" />
              </label>
            </div>
            <div className="bg-white rounded-lg border border-stone-200 p-4">
              <h4 className="text-stone-700 text-sm font-medium mb-2">SEO</h4>
              <label className="block text-stone-500 text-xs mb-1">Meta opis</label>
              <textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} rows={2} className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 resize-none mb-2" data-testid="post-meta-desc" />
              <label className="block text-stone-500 text-xs mb-1">Ključne besede</label>
              <input type="text" value={metaKw} onChange={e => setMetaKw(e.target.value)} className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2" data-testid="post-meta-kw" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;
